<script lang="ts">
import { BarcodeScanner, type Barcode } from '@capacitor-mlkit/barcode-scanning';
import type { PluginListenerHandle } from '@capacitor/core';
import { useTemplateRef } from 'vue';

// Increases with every scanner opened. The native camera is shared, so only the newest scanner may stop it.
let latestScannerId = 0;
</script>

<script setup lang="ts">
interface Props {
  /** Dismisses this scanner's own modal. */
  dismiss: (value?: string) => Promise<boolean>;
}

const props = defineProps<Props>();

const SCANNING_CLASS = 'barcode-scanning-active';

const { t } = useEkI18n();
const { openToast } = useUserMessages();
const settingsStore = useSettingsStore();
const platformStore = usePlatformStore();

const scannerId = ++latestScannerId;
const video = useTemplateRef('video');
const isScanPressed = ref(false);
let listener: PluginListenerHandle | undefined;
let isClosing = false;
// Set once the scanner is closing or unmounted, so a slow start (e.g. the permission prompt) does not turn the camera on afterwards.
let isStopped = false;

// Kept locally so a change applies at once; saving the setting is async.
// The stored setting is "show scan button", which is the opposite of auto scan.
const autoScan = ref(!settingsStore.settings.showScannerButton);

watch(autoScan, value => {
  isScanPressed.value = false;
  settingsStore.set('showScannerButton', !value);
});

function isLatestScanner() {
  return scannerId === latestScannerId;
}

async function canScan() {
  if (platformStore.isWeb) {
    const { supported } = await BarcodeScanner.isSupported();

    if (!supported) {
      openToast(t('materials.barcode.notSupported'), ToastType.ERROR);
    }

    return supported;
  }

  const { camera } = await BarcodeScanner.requestPermissions();
  const isGranted = camera === 'granted' || camera === 'limited';

  if (!isGranted) {
    openToast(t('qa.couldNotOpenCamera'), ToastType.ERROR);
  }

  return isGranted;
}

async function start() {
  try {
    if (!await canScan()) {
      await close();

      return;
    }

    if (isStopped) {
      return;
    }

    document.documentElement.classList.add(SCANNING_CLASS);
    listener = await BarcodeScanner.addListener('barcodesScanned', event => handleBarcodes(event.barcodes));

    // The scanner was closed while the listener was being registered.
    if (isStopped) {
      await stop();

      return;
    }

    // The video element is only used on web. On iOS/Android the camera is shown behind the transparent web view.
    await BarcodeScanner.startScan(platformStore.isWeb ? { videoElement: video.value ?? undefined } : undefined);
  } catch {
    openToast(t('qa.couldNotOpenCamera'), ToastType.ERROR);
    await close();
  }

  // The scanner was closed while the camera was starting.
  if (isStopped) {
    await stop();
  }
}

async function stop() {
  isStopped = true;

  if (isLatestScanner()) {
    document.documentElement.classList.remove(SCANNING_CLASS);
  }

  await listener?.remove().catch(() => undefined);
  listener = undefined;

  if (isLatestScanner()) {
    await BarcodeScanner.stopScan().catch(() => undefined);
  }
}

function handleBarcodes(barcodes: Barcode[]) {
  const value = barcodes[0]?.rawValue;

  if (!value || (!autoScan.value && !isScanPressed.value)) {
    return;
  }

  void close(value);
}

async function close(value?: string) {
  // Already closed elsewhere (e.g. swiped away); dismissing again could close another modal.
  if (isClosing || isStopped) {
    return;
  }

  isClosing = true;

  try {
    await stop();
  } finally {
    await props.dismiss(value);
  }
}

onMounted(start);
onBeforeUnmount(stop);
</script>

<template>
  <IonContent
    class="barcode-scanner"
    :scroll-y="false"
  >
    <div class="barcode-scanner__layout">
      <div class="barcode-scanner__camera">
        <video
          v-if="platformStore.isWeb"
          ref="video"
          class="barcode-scanner__video"
          muted
          playsinline
        />
        <div class="barcode-scanner__frame" />
        <IonButton
          class="barcode-scanner__close"
          fill="clear"
          :aria-label="t('buttons.close')"
          @click="close()"
        >
          <IonIcon class="fa-light fa-xmark" />
        </IonButton>
      </div>

      <div class="barcode-scanner__controls">
        <IonButton
          class="barcode-scanner__scan"
          :disabled="autoScan"
          :fill="isScanPressed ? 'outline' : 'solid'"
          @click="isScanPressed = !isScanPressed"
        >
          {{ isScanPressed ? t('materials.barcode.scanning') : t('materials.barcode.scan') }}
        </IonButton>
        <IonToggle
          v-model="autoScan"
          label-placement="end"
        >
          {{ t('materials.barcode.autoScan') }}
        </IonToggle>
      </div>
    </div>
  </IonContent>
</template>

<style>
/* While scanning, the native camera is drawn behind the web view, so everything except the scanner must be see-through. */
html.barcode-scanning-active,
html.barcode-scanning-active body {
  background: transparent;
}

html.barcode-scanning-active body {
  visibility: hidden;
  --ion-background-color: transparent;
}

html.barcode-scanning-active ion-toast {
  visibility: visible;
}

/* !important is needed to beat the global ion-modal backdrop and shadow in main.css. */
.barcode-scanner-modal {
  visibility: visible;
  --width: 100%;
  --height: 100%;
  --border-radius: 0;
  --background: transparent;
  --backdrop-opacity: 0 !important;
  --box-shadow: none !important;
}
</style>

<style scoped>
.barcode-scanner {
  --background: transparent;
}

.barcode-scanner__layout {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.barcode-scanner__camera {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.barcode-scanner__video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000000;
}

.barcode-scanner__frame {
  position: relative;
  width: 75%;
  max-width: 400px;
  aspect-ratio: 2 / 1;
  border: 2px solid #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.4);
}

.barcode-scanner__close {
  position: absolute;
  top: calc(env(safe-area-inset-top) + 8px);
  right: 8px;
  font-size: 1.5rem;
  --color: #FFFFFF;
}

.barcode-scanner__controls {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 16px calc(env(safe-area-inset-bottom) + 16px);
  background: var(--ion-color-bar);
  color: #FFFFFF;
}

.barcode-scanner__scan {
  flex: 1;
}
</style>
